import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getPackageById, getHistory, getQRcode, getLatestVersion, getAzirConfig } from './api';
import {
  IMG,
  PackageType,
  USER_AGENT,
  SORT_MAPPING,
  DEFAULT_EMPTY_TEXT,
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGESIZE,
  DEFAULT_TOOLTIP_TIMEOUT,
  IOS_TESTFLIGHT_INSTALL_URL,
  KOL_CN_TESTFLIGHT_URL,
  KOL_OVERSEAS_TESTFLIGHT_URL,
  QRcodeType,
} from './data';
import './App.css';

const App = () => {
  const [qrUrl, setQrUrl] = useState<string>('');
  const [packageInfo, setPackageInfo] = useState<PackageType>();
  const [historyList, setHistoryList] = useState<PackageType[]>([]);
  const [current, setCurrent] = useState<number>(1);
  const [showMore, setShowMore] = useState<boolean>(true);
  const [showTip, setShowTip] = useState<boolean>(false);
  const [showMask, setShowMask] = useState<boolean>(false);
  const [errMsg, setErrMes] = useState<string>('');
  const UA = useMemo(() => navigator.userAgent.toLowerCase(), []);
  const isWeixinOrDing = useMemo(() => (UA.indexOf('micromessenger') > -1) || (UA.indexOf('dingtalk') > -1), [UA]);
  const [version, sort, product_sort, platformParam] = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const version = urlParams.get('version');
    const sort = urlParams.get('sort') as QRcodeType['sort'];
    const product_sort = urlParams.get('productFilter');
    const platformParam = urlParams.get('platform');
    return [version, sort, product_sort ?? undefined, platformParam ?? undefined];
  }, []);
  const device = useMemo(() => {
    if (platformParam === 'ios' || platformParam === 'android') return platformParam;
    return /iphone|ipad|ipod|mac/.test(UA) ? USER_AGENT.ios : USER_AGENT.android;
  }, [UA, platformParam]);

  const showErr = useCallback((msg: string) => {
    setErrMes(msg);
    setTimeout(() => setErrMes(''), DEFAULT_TOOLTIP_TIMEOUT);
  }, []);

  // 二维码及 logo 的 sort：按 productSort 包含关键词模糊匹配
  const getSort = (productSort: string | undefined): QRcodeType['sort'] => {
    if (!productSort) return SORT_MAPPING.beta.sort;
    if (SORT_MAPPING.internal.includes.some(k => productSort.includes(k))) return SORT_MAPPING.internal.sort;
    if (SORT_MAPPING.external.includes.some(k => productSort.includes(k))) return SORT_MAPPING.external.sort;
    return SORT_MAPPING.beta.sort;
  };

  const createLink = useCallback((data: PackageType, isGetConfig: boolean, iosTfUrl: string) => {
    const ps = data?.productSort ?? '';
    const isIos = data?.versionType === USER_AGENT.ios;

    // iOS 安装链接：kol_国内 / kol_海外|国外 / 含 beta → TestFlight；否则 plist 或配置 TF 兜底
    let downloadUrl = '';
    if (isIos) {
      if (ps.startsWith('kol_国内')) {
        downloadUrl = KOL_CN_TESTFLIGHT_URL;
      } else if (ps.startsWith('kol_海外') || ps.startsWith('kol_国外')) {
        downloadUrl = KOL_OVERSEAS_TESTFLIGHT_URL;
      } else if (ps.includes('beta')) {
        if (isGetConfig && !iosTfUrl) return;
        downloadUrl = iosTfUrl;
      } else {
        if (data?.artifactPlist) {
          downloadUrl = `itms-services://?action=download-manifest&url=${data.artifactPlist}`;
        } else if (isGetConfig && iosTfUrl) {
          downloadUrl = iosTfUrl;
        }
      }
    }

    if (data?.versionType === USER_AGENT.android) {
      const [artDomain, pkgLink] = data.artifactUrl.split('artifactory/APP');
      downloadUrl = `${artDomain}ui/api/v1/download?repoKey=APP&path=${pkgLink}&isNativeBrowsing=true`;
    }

    const hasInstallLink = !!downloadUrl || !!data?.artifactUrl;
    if (!hasInstallLink) return;
    isWeixinOrDing && setShowTip(true);

    const container = document.getElementById('install');
    container?.firstChild && container?.removeChild(container.firstChild);

    const linkElement = document.createElement('a');
    linkElement.className = 'install-btn';
    linkElement.innerText = '安装';
    if (isWeixinOrDing) {
      linkElement.onclick = () => setShowMask(true);
    } else {
      linkElement.onclick = () => { linkElement.innerText = '安装中...'; };
      linkElement.href = downloadUrl;
    }
    container?.appendChild(linkElement);
  }, [isWeixinOrDing]);

  const getInitialData = useCallback(async () => {
    let packageData;
    const res = version ? await getPackageById(version) : await getLatestVersion(device, product_sort);
    packageData = res.data;

    if (!res.success) {
      showErr(`获取应用信息失败,${res.message}`);
    }
    if (!res.data) {
      showErr('未找到该版本');
    }

    // 仅当 iOS 且（含 beta 非 KOL 需配置 TF，或 无 plist 需配置兜底）时请求配置
    const ps = packageData?.productSort ?? '';
    const isKolTf =
      ps.startsWith('kol_国内') || ps.startsWith('kol_海外') || ps.startsWith('kol_国外');
    const isGetConfig =
      packageData?.versionType === USER_AGENT.ios &&
      ((ps.includes('beta') && !isKolTf) || !packageData?.artifactPlist);
    const configRes = isGetConfig ? await getAzirConfig() : {};
    const env = window.location.href.includes('test') ? 'test' : 'prod';
    const qrcodeRes = await getQRcode({
      env,
      version,
      sort: sort ?? getSort(packageData?.productSort),
    });

    if (!qrcodeRes.success) {
      showErr(`获取二维码失败,${qrcodeRes.message}`);
    }
    setPackageInfo(packageData);
    setQrUrl(qrcodeRes.data);
    createLink(packageData, isGetConfig, configRes.data?.value);

    getHistory({
      pageSize: DEFAULT_PAGESIZE,
      current: DEFAULT_CURRENT_PAGE,
      versionType: device,
      ...(product_sort && { productSort: product_sort }),
    })
      .then(res => {
        if (res.success) {
          const { packages, pagination } = res;
          setHistoryList(packages);
          setShowMore(pagination.total > DEFAULT_PAGESIZE);
        } else {
          showErr('获取历史版本失败');
        }
      })
      .catch(err => showErr('获取历史版本失败'));
  }, [version, sort, device, product_sort, createLink, showErr]);

  const gotoHistory = useCallback((buildTimestamp: string) => {
    const { host, protocol } = window.location;
    const params = new URLSearchParams(window.location.search);
    params.set('version', buildTimestamp);
    window.location.href = `${protocol}//${host}?${params.toString()}`;
  }, []);

  const lookMore = useCallback(() => {
    getHistory({
      pageSize: DEFAULT_PAGESIZE,
      current: current + 1,
      versionType: device,
      ...(product_sort && { productSort: product_sort }),
    }).then(res => {
      if (res.success) {
        const { packages, pagination } = res;
        if (historyList.length < pagination.total) {
          setHistoryList((prev) => prev.concat(packages));
          setCurrent(current + 1);
        }
        setShowMore(historyList.length + packages.length < pagination.total);
      } else {
        showErr('获取历史版本失败');
      }
    }).catch(err => showErr('获取历史版本失败'));
  }, [current, device, product_sort, historyList.length, showErr]);

  const Tooltip = useCallback((): JSX.Element => {
    const ps = packageInfo?.productSort ?? '';
    const isIosTf =
      packageInfo?.versionType === USER_AGENT.ios &&
      (ps.startsWith('kol_国内') ||
        ps.startsWith('kol_海外') ||
        ps.startsWith('kol_国外') ||
        ps.includes('beta'));
    if (isIosTf) {
      return <div className='ios-tip' >
        使用 TestFlight App 帮助开发者测试 Beta 版 App<br />
        若未安装，请先在 App Store 中<a href={IOS_TESTFLIGHT_INSTALL_URL}>下载TestFlight</a>
      </div>;
    }

    if (packageInfo?.versionType === USER_AGENT.ios) {
      return <div className='ios-tip' >
        安装完成后需要开启开发者模式才能打开此应用<br />
        (设置 &#8608; 隐私与安全性 &#8608; 下滑到底部 &#8608; 开发者模式)
      </div>;
    }

    if (device === USER_AGENT.android && packageInfo?.versionType !== USER_AGENT.ios && !isWeixinOrDing) {
      return <div className='android-tip' >
        若无法下载，请使用第三方浏览器（如UC浏览器、QQ浏览器等）进行下载
      </div>;
    }

    return <></>;
  }, [device, isWeixinOrDing, packageInfo?.versionType, packageInfo?.productSort]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  return (
    <div className='App'>
      <img className={showMask ? 'wx-ding-mask fade-in' : 'wx-ding-mask'} src={IMG.tips} alt='wx-ding-mask' onClick={() => setShowMask(false)} />
      <img className='logo' src={IMG.logo} alt='logo' />
      <div className='name' >云鲸智能</div>
      <div className='device' >适用于{packageInfo?.versionType ?? device}设备</div>
      <Tooltip />
      {qrUrl && <img className='qrcode' src={qrUrl} alt='QRcode' />}
      <div id='install' />
      {packageInfo && <>
        <div className='info-box'>
          <div className='info-title' >应用信息</div>
          <div className='info-block' >
            <div >大小</div>  <div>{packageInfo?.packageSize ?? DEFAULT_EMPTY_TEXT}</div>
          </div>
          <div className='info-block' >
            <div >版本</div>  <div>{packageInfo?.versionNumber ?? DEFAULT_EMPTY_TEXT}</div>
          </div>
          <div className='info-block' >
            <div >更新时间</div>  <div>{packageInfo?.buildTime ?? DEFAULT_EMPTY_TEXT}</div>
          </div>
        </div>
        <div className='info-box' >
          <div className='info-title'>更新说明</div>
          <div className='info-block'>Commit Id: {packageInfo?.commitidInfo ?? DEFAULT_EMPTY_TEXT}</div>
          <div className='info-block'> {packageInfo?.updateDescription} </div>
        </div>
      </>}
      <div className='info-box' >
        <div className='info-title'>历史版本</div>
        {historyList?.map(item => <div key={item.id} className='info-block' onClick={() => gotoHistory(item.buildTimestamp)}>
          <div >{item.versionNumber}</div>
          <div>{item.buildTime}</div>
        </div>)}
        {showMore && <div onClick={lookMore} className='look-more'> 查看更多&#8609; </div>}
      </div>
      <div className={showTip ? 'tooltip fade-in' : 'tooltip'}> 点击右上角按钮，然后在弹出的菜单中点击在浏览器打开，即可安装&#8599; </div>
      <div className={errMsg.length ? 'tooltip err-message fade-in' : 'tooltip err-message'}>{errMsg}</div>
    </div>
  );
}

export default App;
